import { Box, Stack, useTheme } from "@mui/material";
import { useSetAtom } from "jotai";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApplicationInformationQuery } from "web_myplann/Api/Miscellaneous/useApplicationInformationQuery";
import { DashboardCarouselDots } from "web_myplann/Dashboard/Components/DashboardCarouselDots";
import { DashboardSkeleton } from "web_myplann/Dashboard/Components/DashboardSkeleton";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { CarrouselDots } from "web_myplann/Shared/Components/CarrouselDots";
import type { NativePullToRefreshProps } from "web_myplann/Shared/Components/NativePullToRefresh";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { useOnScreen } from "web_myplann/Shared/Hooks/useOnScreen";
import {
	type SwipeableComponentRef,
	useSwipeable,
} from "web_myplann/Shared/Hooks/useSwipeable";
import { useDashboardAnimation } from "../Hooks/useDashboardAnimation";
import { useDashboardData } from "../Hooks/useDashboardData";
import {
	getSavedDashboardIndex,
	saveDashboardIndex,
} from "../Hooks/useSavedDashboardIndex";
import { dashboardGradientAtom } from "../State/dashboardGradientAtom";
import { useIsDashboardV2 } from "../State/dashboardVariantContext";
import {
	DOTS_BOTTOM_OFFSET,
	DOTS_STRIP_HEIGHT,
} from "../Utils/carouselDotsLayout";
import {
	DASHBOARD_CARD_COLUMN_HEIGHT,
	DASHBOARD_HERO_HEIGHT,
} from "../Utils/dashboardLayout";
import { DashboardCardCarouselResolver } from "./DashboardCardCarouselResolver";
import { DashboardEmptyHeader } from "./DashboardEmptyHeader";
import { DashboardEmptyState } from "./DashboardEmptyState";
import { DashboardGradientHeader } from "./DashboardGradientHeader";

type DashboardTitleCardProps = {
	onEmptyChange?: (isEmpty: boolean) => void;
	pullToRefreshProps?: Omit<NativePullToRefreshProps, "children">;
	children?: React.ReactNode;
};

// Fixed height of the old-design (V1) card, kept as-is so V1 doesn't change; V2 derives its height from the layout constants below.
const CARD_CONTENT_HEIGHT = 224;
const CARD_V2_CONTENT_HEIGHT = DASHBOARD_CARD_COLUMN_HEIGHT + DOTS_STRIP_HEIGHT;
const GRADIENT_HEIGHT = DASHBOARD_HERO_HEIGHT;

export const DashboardTitleCard = ({
	onEmptyChange,
	pullToRefreshProps,
	children,
}: DashboardTitleCardProps) => {
	const { palette } = useTheme();
	const navigate = useNavigate();
	const isDashboardV2 = useIsDashboardV2();
	const DotsComponent = isDashboardV2 ? DashboardCarouselDots : CarrouselDots;
	const setDashboardGradient = useSetAtom(dashboardGradientAtom);

	const { data: applicationInformation } = useApplicationInformationQuery();

	const carrouselRef = useRef<SwipeableComponentRef>(null);
	const colorAreaRef = useRef<HTMLDivElement>(null);

	const [contentPush, setContentPush] = useState(0);

	const { dashboards, isLoading, isEmpty } = useDashboardData();

	const isCardVisible = useOnScreen(colorAreaRef, !isEmpty);

	useLayoutEffect(() => {
		onEmptyChange?.(isEmpty && !isLoading);
	}, [onEmptyChange, isEmpty, isLoading]);

	const savedIndex = Math.min(
		Math.max(getSavedDashboardIndex(), 0),
		Math.max(dashboards.length - 1, 0),
	);

	const {
		dashboard,
		activeIndex,
		isAnimating,
		previousDashboard,
		animationDirection,
		handleDashboardChange: onDashboardChange,
		calculateDirection,
	} = useDashboardAnimation(dashboards, savedIndex, !isEmpty, isCardVisible);

	useEffect(() => {
		setDashboardGradient(isEmpty ? undefined : dashboard?.gradientTo);
	}, [dashboard?.gradientTo, isEmpty, setDashboardGradient]);

	const navigateToDashboardDetail = () => {
		navigate(AppRouting.MyPlann.DashboardDetail.path(dashboard.overviewType));
	};

	const handleDashboardChange = (
		newIndex: number,
		direction: "left" | "right" | null,
	) => {
		saveDashboardIndex(newIndex);
		onDashboardChange(newIndex, direction);
	};

	const {
		handlePointerDown,
		handlePointerMove,
		handlePointerEnd,
		startChange,
		stopChange,
	} = useSwipeable(carrouselRef, false);

	const handleTooltipToggle = (open: boolean) => {
		if (open) {
			stopChange();
		} else {
			startChange();
		}
	};

	const title = applicationInformation?.hspName ?? "SAB servis";

	if (isEmpty) {
		return (
			<Stack>
				{isDashboardV2 && !isLoading ? (
					<DashboardEmptyState />
				) : (
					<DashboardEmptyHeader title={title} />
				)}
				<NativePullToRefresh {...pullToRefreshProps} spinnerOffset={-25}>
					{isLoading && (
						<Stack px={1.6} pt={1.6}>
							<DashboardSkeleton height={180} />
						</Stack>
					)}
					{children}
				</NativePullToRefresh>
			</Stack>
		);
	}

	return (
		<Stack position="relative">
			<DashboardGradientHeader
				title={title}
				gradientTo={dashboard.gradientTo}
				gradientFrom={dashboard.gradientFrom}
				gradientHeight={GRADIENT_HEIGHT + contentPush}
				animateHeight={contentPush === 0}
			/>

			<Box
				ref={colorAreaRef}
				position="absolute"
				top={GRADIENT_HEIGHT - 1}
				left={0}
				right={0}
				height="1px"
				zIndex={0}
				sx={{ pointerEvents: "none" }}
			/>

			<NativePullToRefresh
				{...pullToRefreshProps}
				disableContentPush
				pushContentWhenRefreshing
				onContentPushChange={setContentPush}
				spinnerColor={palette.darkmodeText.white}
				spinnerOffset={-30}
			>
				<Box
					height={isDashboardV2 ? CARD_V2_CONTENT_HEIGHT : CARD_CONTENT_HEIGHT}
					position="relative"
					display="flex"
					alignItems="center"
					justifyContent="center"
					overflow="hidden"
					zIndex={1}
					sx={{ userSelect: "none", touchAction: "pan-y" }}
					onPointerDown={handlePointerDown}
					onPointerMove={handlePointerMove}
					onPointerUp={handlePointerEnd}
					onPointerCancel={handlePointerEnd}
				>
					{isLoading ? (
						<Box width="100%" pb={3} px={1.6}>
							<DashboardSkeleton height={180} />
						</Box>
					) : (
						<DashboardCardCarouselResolver
							dashboard={dashboard}
							previousDashboard={previousDashboard}
							isAnimating={isAnimating}
							animationDirection={animationDirection}
							navigateToDashboardDetail={navigateToDashboardDetail}
							onTooltipToggle={handleTooltipToggle}
						/>
					)}

					<Stack
						position="absolute"
						bottom={DOTS_BOTTOM_OFFSET}
						left="50%"
						sx={{ transform: "translateX(-50%)" }}
						zIndex={2}
					>
						<DotsComponent
							ref={carrouselRef}
							length={dashboards.length}
							currentValue={activeIndex}
							onClick={(val, direction) => {
								stopChange();
								const animDirection =
									direction || calculateDirection(dashboard, val);
								handleDashboardChange(val, animDirection);
								startChange();
							}}
						/>
					</Stack>
				</Box>
				{children}
			</NativePullToRefresh>
		</Stack>
	);
};
